import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

interface CostVarianceItem {
  stable_cost: number;
  actual_cost: number;
  difference: number;
  variance_percent: number;
  status: 'high' | 'low' | 'normal';
}

interface CostVariance {
  actual_cost_per_lb: number;
  mash: CostVarianceItem | null;
  pellet: CostVarianceItem | null;
}

interface FeedFormula {
  id: number;
  name: string;
  sku: string;
  animal_type: string;
  animal_type_display: string;
  grade: string;
  grade_display: string;
  bag_size: number;
  stable_cost_mash: number | null;
  stable_cost_pellet: number | null;
  calculated_cost_per_lb: number | null;
  cost_variance: CostVariance | null;
  price_bag_mash: number | null;
  price_bag_pellet: number | null;
  price_byoc_mash: number | null;
  price_byoc_pellet: number | null;
  price_1000lb_mash: number | null;
  price_1000lb_pellet: number | null;
  price_2000lb_mash: number | null;
  price_2000lb_pellet: number | null;
  margin_mash: number | null;
  margin_pellet: number | null;
  updated_at: string | null;
  prices_locked: boolean;
  prices_locked_at: string | null;
  prices_locked_by: string | null;
}

interface StoreClubProduct {
  id: number;
  name: string;
  brand: string;
  category: string;
  category_display: string;
  sku: string;
  size_lbs: number | null;
  price: number | null;
  is_byoc: boolean;
  updated_at: string | null;
}

interface PriceChange {
  id: number;
  changed_by: string;
  changed_at: string;
  entity_type: string;
  entity_id: number;
  entity_name: string;
  changes: Record<string, { old: number | null; new: number | null }>;
  notes: string;
}

interface PricingStats {
  feed_formulas: {
    total: number;
    with_prices: number;
    missing_prices: number;
    average_margin: number | null;
    low_margin_count: number;
  };
  store_club_products: {
    total: number;
    with_prices: number;
    missing_prices: number;
  };
  price_changes: {
    total_changes: number;
    last_change: string | null;
    last_changed_by: string | null;
  };
}

interface PricingSettings {
  pelleting_cost_per_lb: string;
  taxes_per_lb: string;
  bag_material_cost: string;
  labor_per_bag: string;
  bag_profit_min: string;
  profit_per_lb_byoc: string;
  profit_per_lb_1000: string;
  profit_per_lb_2000: string;
  updated_at: string | null;
  updated_by: string | null;
}

type TabType = 'formulas' | 'products' | 'catalog' | 'history';

// Mirrors ADDON_WEIGHT_UNITS in order_service.py. A unit in this list means
// the item is priced by the pound, so its weight IS its quantity and asking
// for a per-item weight would be meaningless.
const ADDON_WEIGHT_UNITS = ['lb', 'lbs', 'pound', 'pounds', '#'];
const UNIT_IS_WEIGHT = (unit: string) =>
  ADDON_WEIGHT_UNITS.includes((unit || '').trim().toLowerCase());

interface CatalogProduct {
  id: number;
  name: string;
  sku: string;
  description: string;
  product_type: string;
  product_type_display: string;
  default_price: string;
  unit: string;
  taxable: boolean;
  is_active: boolean;
  qb_item_id: string | null;
  qb_item_name: string;
  qb_item_type: string;
  last_qb_sync: string | null;
  track_inventory: boolean;
  current_stock: string;
  // Add-ons: whether the order form may offer this, and what one weighs.
  sellable_as_addon: boolean;
  addon_weight_lbs: string | null;
  priced_by_weight: boolean;
  feed_formula_id: number | null;
  feed_formula_name: string | null;
}

const PriceManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('formulas');
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [products, setProducts] = useState<StoreClubProduct[]>([]);
  const [history, setHistory] = useState<PriceChange[]>([]);
  const [stats, setStats] = useState<PricingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editing state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Bulk update state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkField, setBulkField] = useState('price_bag_mash');
  const [bulkPercentage, setBulkPercentage] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');

  // Filter
  const [animalTypeFilter, setAnimalTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Product catalog state
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogTypeFilter, setCatalogTypeFilter] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogSyncing, setCatalogSyncing] = useState(false);
  const [editingCatalogId, setEditingCatalogId] = useState<number | null>(null);
  const [catalogEditValues, setCatalogEditValues] = useState<Record<string, string>>({});

  // Pricing Settings state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [pricingSettings, setPricingSettings] = useState<PricingSettings | null>(null);
  const [settingsValues, setSettingsValues] = useState<Record<string, string>>({});
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[PriceManagement] Loading pricing data...');

      // Load data in sequence to better debug any issues
      let statsData = null;
      let formulasData: FeedFormula[] = [];
      let productsData: StoreClubProduct[] = [];
      let historyData: PriceChange[] = [];

      try {
        const statsRes = await api.get('/admin/pricing/stats/');
        statsData = statsRes.data;
        console.log('[PriceManagement] Stats loaded:', statsData);
      } catch (e: any) {
        console.error('[PriceManagement] Stats error:', e.response?.status, e.response?.data);
      }

      try {
        console.log('[PriceManagement] Fetching feed formulas...');
        const formulasRes = await api.get('/admin/pricing/feed-formulas/');
        console.log('[PriceManagement] Formulas response:', formulasRes);
        formulasData = formulasRes.data;
        console.log('[PriceManagement] Formulas loaded:', formulasData.length, 'items:', formulasData);
      } catch (e: any) {
        console.error('[PriceManagement] Formulas error:', e.response?.status, e.response?.data, e.message);
      }

      try {
        const productsRes = await api.get('/admin/pricing/store-club-products/');
        productsData = productsRes.data;
        console.log('[PriceManagement] Products loaded:', productsData.length);
      } catch (e: any) {
        console.error('[PriceManagement] Products error:', e.response?.status, e.response?.data);
      }

      try {
        const catalogRes = await api.get('/products/');
        setCatalogProducts(catalogRes.data || []);
      } catch (e: any) {
        console.error('[PriceManagement] Catalog error:', e.response?.status);
      }

      try {
        const historyRes = await api.get('/admin/pricing/history/?limit=20');
        historyData = historyRes.data;
        console.log('[PriceManagement] History loaded:', historyData.length);
      } catch (e: any) {
        console.error('[PriceManagement] History error:', e.response?.status, e.response?.data);
      }

      setStats(statsData);
      setFormulas(formulasData);
      setProducts(productsData);
      setHistory(historyData);

      // Show error only if all major endpoints failed
      if (!statsData && formulasData.length === 0 && productsData.length === 0) {
        setError('Failed to load pricing data. Please check your permissions.');
      } else if (statsData && statsData.feed_formulas.total > 0 && formulasData.length === 0) {
        // Stats show formulas exist but we couldn't load the list
        console.warn('[PriceManagement] Stats show formulas but list is empty - possible API issue');
        setError('Warning: Could not load formula list. Stats show ' + statsData.feed_formulas.total + ' formulas exist.');
      }
    } catch (err: any) {
      console.error('[PriceManagement] General error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditFormula = (formula: FeedFormula) => {
    setEditingId(formula.id);
    setEditValues({
      price_bag_mash: formula.price_bag_mash?.toString() || '',
      price_bag_pellet: formula.price_bag_pellet?.toString() || '',
      price_byoc_mash: formula.price_byoc_mash?.toString() || '',
      price_byoc_pellet: formula.price_byoc_pellet?.toString() || '',
      price_1000lb_mash: formula.price_1000lb_mash?.toString() || '',
      price_1000lb_pellet: formula.price_1000lb_pellet?.toString() || '',
      price_2000lb_mash: formula.price_2000lb_mash?.toString() || '',
      price_2000lb_pellet: formula.price_2000lb_pellet?.toString() || '',
    });
  };

  const handleSaveFormula = async (formulaId: number) => {
    setSaving(true);
    try {
      await api.patch(`/admin/pricing/feed-formulas/${formulaId}/`, {
        price_bag_mash: editValues.price_bag_mash ? parseFloat(editValues.price_bag_mash) : null,
        price_bag_pellet: editValues.price_bag_pellet ? parseFloat(editValues.price_bag_pellet) : null,
        price_byoc_mash: editValues.price_byoc_mash ? parseFloat(editValues.price_byoc_mash) : null,
        price_byoc_pellet: editValues.price_byoc_pellet ? parseFloat(editValues.price_byoc_pellet) : null,
        price_1000lb_mash: editValues.price_1000lb_mash ? parseFloat(editValues.price_1000lb_mash) : null,
        price_1000lb_pellet: editValues.price_1000lb_pellet ? parseFloat(editValues.price_1000lb_pellet) : null,
        price_2000lb_mash: editValues.price_2000lb_mash ? parseFloat(editValues.price_2000lb_mash) : null,
        price_2000lb_pellet: editValues.price_2000lb_pellet ? parseFloat(editValues.price_2000lb_pellet) : null,
      });
      setEditingId(null);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEditProduct = (product: StoreClubProduct) => {
    setEditingId(product.id);
    setEditValues({
      price: product.price?.toString() || '',
    });
  };

  const handleSaveProduct = async (productId: number) => {
    setSaving(true);
    try {
      await api.patch(`/admin/pricing/store-club-products/${productId}/`, {
        price: editValues.price ? parseFloat(editValues.price) : null,
      });
      setEditingId(null);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalcPrices = async (formulaId: number, formulaName: string) => {
    if (!window.confirm(`Recalculate prices for "${formulaName}" based on ingredient costs?\n\nThis will apply the standard markup formula:\n- Bags: (cost × bag_size) + $1.50 + $12.00\n- BYOC: cost + $0.30/lb\n- 1000-1999 lb: cost + $0.25/lb\n- 2000+ lb: cost + $0.19/lb`)) {
      return;
    }

    setSaving(true);
    try {
      await api.post(`/admin/pricing/feed-formulas/${formulaId}/apply-suggested/`);
      await loadData();
      alert('Prices updated successfully!');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to recalculate prices');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLock = async (formulaId: number, currentlyLocked: boolean) => {
    if (currentlyLocked) {
      // Unlocking - require confirmation
      if (!window.confirm('Unlock prices for editing?\n\nThis will allow prices to be modified.')) {
        return;
      }
    } else {
      // Locking - no confirmation needed, but show feedback
      if (!window.confirm('Lock prices to prevent accidental changes?')) {
        return;
      }
    }

    setSaving(true);
    try {
      await api.post(`/admin/pricing/feed-formulas/${formulaId}/toggle-lock/`, {
        action: currentlyLocked ? 'unlock' : 'lock',
        confirm: currentlyLocked ? true : undefined  // confirm required when unlocking
      });
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to toggle lock');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncCatalog = async () => {
    setCatalogSyncing(true);
    try {
      const res = await api.post('/quickbooks/sync-items/');
      const msg = res.data;
      alert(`Sync complete: ${msg.products_created || 0} new products, ${msg.products_updated || 0} updated`);
      // Reload catalog
      const catalogRes = await api.get('/products/');
      setCatalogProducts(catalogRes.data || []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Sync failed');
    } finally {
      setCatalogSyncing(false);
    }
  };

  const handleEditCatalogProduct = (product: CatalogProduct) => {
    setEditingCatalogId(product.id);
    setCatalogEditValues({
      sku: product.sku || '',
      default_price: product.default_price || '0.00',
      unit: product.unit || 'each',
      product_type: product.product_type || 'other',
      sellable_as_addon: product.sellable_as_addon ? 'true' : 'false',
      addon_weight_lbs: product.addon_weight_lbs ?? '',
    });
  };

  const handleSaveCatalogProduct = async (productId: number) => {
    setSaving(true);
    try {
      const weightRaw = (catalogEditValues.addon_weight_lbs || '').trim();
      await api.patch(`/products/${productId}/`, {
        sku: catalogEditValues.sku,
        default_price: parseFloat(catalogEditValues.default_price) || 0,
        unit: catalogEditValues.unit,
        product_type: catalogEditValues.product_type,
        sellable_as_addon: catalogEditValues.sellable_as_addon === 'true',
        // Blank means "no weight on file", which is a real state the order
        // form relies on — so send null, not 0. Zero would read as a weighed
        // item that happens to weigh nothing.
        addon_weight_lbs: weightRaw === '' ? null : parseFloat(weightRaw),
      });
      setEditingCatalogId(null);
      const catalogRes = await api.get('/products/');
      setCatalogProducts(catalogRes.data || []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCatalogActive = async (product: CatalogProduct) => {
    try {
      await api.patch(`/products/${product.id}/`, { is_active: !product.is_active });
      const catalogRes = await api.get('/products/');
      setCatalogProducts(catalogRes.data || []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update');
    }
  };

  const filteredCatalogProducts = catalogProducts.filter(p => {
    if (catalogTypeFilter && p.product_type !== catalogTypeFilter) return false;
    if (catalogSearch) {
      const s = catalogSearch.toLowerCase();
      return p.name.toLowerCase().includes(s) || (p.sku || '').toLowerCase().includes(s);
    }
    return true;
  });

  const catalogTypeCounts = catalogProducts.reduce((acc, p) => {
    acc[p.product_type] = (acc[p.product_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const handleBulkUpdate = async () => {
    if (!bulkPercentage) {
      alert('Please enter a percentage');
      return;
    }

    const percentage = parseFloat(bulkPercentage);
    if (isNaN(percentage)) {
      alert('Invalid percentage');
      return;
    }

    if (!window.confirm(`Apply ${percentage > 0 ? '+' : ''}${percentage}% to ${activeTab === 'formulas' ? bulkField : 'all prices'}?`)) {
      return;
    }

    setSaving(true);
    try {
      if (activeTab === 'formulas') {
        await api.post('/admin/pricing/feed-formulas/bulk/', {
          action: 'percentage',
          field: bulkField,
          value: percentage,
          notes: `Bulk ${percentage}% adjustment`,
        });
      } else {
        await api.post('/admin/pricing/store-club-products/bulk/', {
          action: 'percentage',
          value: percentage,
          category: bulkCategory || undefined,
          notes: `Bulk ${percentage}% adjustment`,
        });
      }
      setShowBulkModal(false);
      setBulkPercentage('');
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to apply bulk update');
    } finally {
      setSaving(false);
    }
  };

  const loadPricingSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await api.get('/admin/pricing/settings/');
      setPricingSettings(res.data);
      setSettingsValues({
        pelleting_cost_per_lb: res.data.pelleting_cost_per_lb || '0.0200',
        taxes_per_lb: res.data.taxes_per_lb || '0.0000',
        bag_material_cost: res.data.bag_material_cost || '0.50',
        labor_per_bag: res.data.labor_per_bag || '1.50',
        bag_profit_min: res.data.bag_profit_min || '12.00',
        profit_per_lb_byoc: res.data.profit_per_lb_byoc || '0.3000',
        profit_per_lb_1000: res.data.profit_per_lb_1000 || '0.2500',
        profit_per_lb_2000: res.data.profit_per_lb_2000 || '0.1900',
      });
    } catch (err: any) {
      console.error('[PriceManagement] Settings load error:', err);
      alert(err.response?.data?.error || 'Failed to load pricing settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleOpenSettings = () => {
    setShowSettingsModal(true);
    loadPricingSettings();
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      await api.patch('/admin/pricing/settings/update/', {
        pelleting_cost_per_lb: parseFloat(settingsValues.pelleting_cost_per_lb) || 0,
        taxes_per_lb: parseFloat(settingsValues.taxes_per_lb) || 0,
        bag_material_cost: parseFloat(settingsValues.bag_material_cost) || 0,
        labor_per_bag: parseFloat(settingsValues.labor_per_bag) || 0,
        bag_profit_min: parseFloat(settingsValues.bag_profit_min) || 0,
        profit_per_lb_byoc: parseFloat(settingsValues.profit_per_lb_byoc) || 0,
        profit_per_lb_1000: parseFloat(settingsValues.profit_per_lb_1000) || 0,
        profit_per_lb_2000: parseFloat(settingsValues.profit_per_lb_2000) || 0,
      });
      setShowSettingsModal(false);
      alert('Pricing settings saved successfully!');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save pricing settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return `$${value.toFixed(2)}`;
  };

  const formatMargin = (margin: number | null | undefined) => {
    if (margin === null || margin === undefined) return '-';
    const color = margin < 20 ? '#e53935' : margin < 30 ? '#fb8c00' : '#43a047';
    return <span style={{ color, fontWeight: 600 }}>{margin.toFixed(1)}%</span>;
  };

  const filteredFormulas = formulas.filter(f => {
    if (animalTypeFilter && f.animal_type !== animalTypeFilter) return false;
    return true;
  });

  const filteredProducts = products.filter(p => {
    if (categoryFilter && p.category !== categoryFilter) return false;
    return true;
  });

  const uniqueAnimalTypes = Array.from(new Set(formulas.map(f => f.animal_type)));
  const uniqueCategories = Array.from(new Set(products.map(p => p.category)));

  if (loading && !stats) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Price Management</h1>
        <div style={styles.loading}>Loading pricing data...</div>
      </div>
    );
  }

  return (
    <div style={styles.container} className="price-mgmt-container">
      <div style={styles.header} className="price-mgmt-header">
        <h1 style={styles.title}>Price Management</h1>
        <div style={styles.headerButtons}>
          <button style={styles.settingsButton} onClick={handleOpenSettings}>
            Markup Settings
          </button>
          <button style={styles.bulkButton} onClick={() => setShowBulkModal(true)}>
            Bulk Price Update
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Stats Cards */}
      {stats && (
        <div style={styles.statsGrid} className="price-mgmt-stats">
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Feed Formulas</div>
            <div style={styles.statValue}>{stats.feed_formulas.with_prices}/{stats.feed_formulas.total}</div>
            <div style={styles.statSubtext}>with prices set</div>
            {stats.feed_formulas.average_margin && (
              <div style={styles.statMargin}>
                Avg margin: {stats.feed_formulas.average_margin.toFixed(1)}%
              </div>
            )}
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Store Club Products</div>
            <div style={styles.statValue}>{stats.store_club_products.with_prices}/{stats.store_club_products.total}</div>
            <div style={styles.statSubtext}>with prices set</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Price Changes</div>
            <div style={styles.statValue}>{stats.price_changes.total_changes}</div>
            <div style={styles.statSubtext}>
              {stats.price_changes.last_changed_by && (
                <>Last by {stats.price_changes.last_changed_by}</>
              )}
            </div>
          </div>
          {stats.feed_formulas.low_margin_count > 0 && (
            <div style={{ ...styles.statCard, borderColor: '#e53935' }}>
              <div style={{ ...styles.statLabel, color: '#e53935' }}>Low Margin Alert</div>
              <div style={{ ...styles.statValue, color: '#e53935' }}>{stats.feed_formulas.low_margin_count}</div>
              <div style={styles.statSubtext}>formulas under 20%</div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs} className="price-mgmt-tabs">
        <button
          style={{ ...styles.tab, ...(activeTab === 'formulas' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('formulas')}
        >
          Feed Formulas ({formulas.length})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'products' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('products')}
        >
          Store Club Products ({products.length})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'catalog' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('catalog')}
        >
          Product Catalog ({catalogProducts.length})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'history' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('history')}
        >
          Change History
        </button>
      </div>

      {/* Feed Formulas Tab */}
      {activeTab === 'formulas' && (
        <div style={styles.tableContainer}>
          <div style={styles.filterRow}>
            <select
              style={styles.filterSelect}
              value={animalTypeFilter}
              onChange={(e) => setAnimalTypeFilter(e.target.value)}
            >
              <option value="">All Animal Types</option>
              {uniqueAnimalTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Formula</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Cost/lb</th>
                <th style={styles.th}>Bag Mash</th>
                <th style={styles.th}>Bag Pellet</th>
                <th style={styles.th}>BYOC Mash</th>
                <th style={styles.th}>BYOC Pellet</th>
                <th style={styles.th}>1000-1999 Mash</th>
                <th style={styles.th}>1000-1999 Pellet</th>
                <th style={styles.th}>2000+ Mash</th>
                <th style={styles.th}>2000+ Pellet</th>
                <th style={styles.th}>Margin</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFormulas.map(formula => (
                <tr key={formula.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.formulaName}>{formula.name}</div>
                    <div style={styles.formulaSku}>{formula.sku || '-'}</div>
                  </td>
                  <td style={styles.td}>{formula.animal_type_display}</td>
                  <td style={styles.td}>
                    {formula.calculated_cost_per_lb ? (
                      <div>
                        <span style={styles.costValue}>${formula.calculated_cost_per_lb.toFixed(4)}</span>
                        {formula.cost_variance?.mash && (
                          <div style={{
                            ...styles.varianceIndicator,
                            ...(formula.cost_variance.mash.status === 'high' ? styles.varianceHigh :
                                formula.cost_variance.mash.status === 'low' ? styles.varianceLow :
                                styles.varianceNormal)
                          }}>
                            {formula.cost_variance.mash.variance_percent > 0 ? '+' : ''}
                            {formula.cost_variance.mash.variance_percent.toFixed(1)}%
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={styles.noCost}>-</span>
                    )}
                  </td>
                  {editingId === formula.id ? (
                    <>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price_bag_mash}
                          onChange={(e) => setEditValues({ ...editValues, price_bag_mash: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price_bag_mash: val.toFixed(2) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price_bag_pellet}
                          onChange={(e) => setEditValues({ ...editValues, price_bag_pellet: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price_bag_pellet: val.toFixed(2) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price_byoc_mash}
                          onChange={(e) => setEditValues({ ...editValues, price_byoc_mash: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price_byoc_mash: val.toFixed(4) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price_byoc_pellet}
                          onChange={(e) => setEditValues({ ...editValues, price_byoc_pellet: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price_byoc_pellet: val.toFixed(4) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price_1000lb_mash}
                          onChange={(e) => setEditValues({ ...editValues, price_1000lb_mash: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price_1000lb_mash: val.toFixed(2) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price_1000lb_pellet}
                          onChange={(e) => setEditValues({ ...editValues, price_1000lb_pellet: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price_1000lb_pellet: val.toFixed(2) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price_2000lb_mash}
                          onChange={(e) => setEditValues({ ...editValues, price_2000lb_mash: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price_2000lb_mash: val.toFixed(2) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price_2000lb_pellet}
                          onChange={(e) => setEditValues({ ...editValues, price_2000lb_pellet: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price_2000lb_pellet: val.toFixed(2) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>-</td>
                      <td style={styles.td}>
                        <button
                          style={styles.saveBtn}
                          onClick={() => handleSaveFormula(formula.id)}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          style={styles.cancelBtn}
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={styles.td}>{formatCurrency(formula.price_bag_mash)}</td>
                      <td style={styles.td}>{formatCurrency(formula.price_bag_pellet)}</td>
                      <td style={styles.td}>{formula.price_byoc_mash ? `$${formula.price_byoc_mash.toFixed(4)}/lb` : '-'}</td>
                      <td style={styles.td}>{formula.price_byoc_pellet ? `$${formula.price_byoc_pellet.toFixed(4)}/lb` : '-'}</td>
                      <td style={styles.td}>
                        {formula.price_1000lb_mash ? (
                          <div>
                            <div>${formula.price_1000lb_mash.toFixed(2)}</div>
                            <div style={styles.perLbText}>${(formula.price_1000lb_mash / 1000).toFixed(4)}/lb</div>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={styles.td}>
                        {formula.price_1000lb_pellet ? (
                          <div>
                            <div>${formula.price_1000lb_pellet.toFixed(2)}</div>
                            <div style={styles.perLbText}>${(formula.price_1000lb_pellet / 1000).toFixed(4)}/lb</div>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={styles.td}>
                        {formula.price_2000lb_mash ? (
                          <div>
                            <div>${formula.price_2000lb_mash.toFixed(2)}</div>
                            <div style={styles.perLbText}>${(formula.price_2000lb_mash / 2000).toFixed(4)}/lb</div>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={styles.td}>
                        {formula.price_2000lb_pellet ? (
                          <div>
                            <div>${formula.price_2000lb_pellet.toFixed(2)}</div>
                            <div style={styles.perLbText}>${(formula.price_2000lb_pellet / 2000).toFixed(4)}/lb</div>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={styles.td}>{formatMargin(formula.margin_mash)}</td>
                      <td style={styles.td}>
                        <div style={styles.actionsCell}>
                          <button
                            style={formula.prices_locked ? styles.lockBtnLocked : styles.lockBtnUnlocked}
                            onClick={() => handleToggleLock(formula.id, formula.prices_locked)}
                            disabled={saving}
                            title={formula.prices_locked ? 'Click to unlock prices' : 'Click to lock prices'}
                          >
                            {formula.prices_locked ? 'Locked' : 'Unlocked'}
                          </button>
                          <button
                            style={formula.prices_locked ? styles.editBtnDisabled : styles.editBtn}
                            onClick={() => handleEditFormula(formula)}
                            disabled={formula.prices_locked}
                            title={formula.prices_locked ? 'Unlock to edit prices' : 'Edit prices'}
                          >
                            Edit
                          </button>
                          {!formula.prices_locked && formula.calculated_cost_per_lb && (
                            <button
                              style={styles.recalcBtn}
                              onClick={() => handleRecalcPrices(formula.id, formula.name)}
                              disabled={saving}
                              title="Recalculate prices from ingredient costs"
                            >
                              Recalc
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Store Club Products Tab */}
      {activeTab === 'products' && (
        <div style={styles.tableContainer}>
          <div style={styles.filterRow}>
            <select
              style={styles.filterSelect}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {uniqueCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Product</th>
                <th style={styles.th}>Brand</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>SKU</th>
                <th style={styles.th}>Size</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => (
                <tr key={product.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.formulaName}>{product.name}</div>
                    {product.is_byoc && <span style={styles.byocBadge}>BYOC</span>}
                  </td>
                  <td style={styles.td}>{product.brand || '-'}</td>
                  <td style={styles.td}>{product.category_display}</td>
                  <td style={styles.td}>{product.sku}</td>
                  <td style={styles.td}>{product.size_lbs ? `${product.size_lbs} lbs` : '-'}</td>
                  {editingId === product.id ? (
                    <>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.editInput}
                          value={editValues.price}
                          onChange={(e) => setEditValues({ ...editValues, price: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setEditValues({ ...editValues, price: val.toFixed(2) });
                            }
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.saveBtn}
                          onClick={() => handleSaveProduct(product.id)}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          style={styles.cancelBtn}
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={styles.td}>{formatCurrency(product.price)}</td>
                      <td style={styles.td}>
                        <button
                          style={styles.editBtn}
                          onClick={() => handleEditProduct(product)}
                        >
                          Edit
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Product Catalog Tab */}
      {activeTab === 'catalog' && (
        <div style={styles.tableContainer}>
          <div style={{ ...styles.filterRow, display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              style={{
                ...styles.bulkButton,
                opacity: catalogSyncing ? 0.6 : 1,
                padding: '0.5rem 1rem',
                fontSize: '0.8125rem',
              }}
              onClick={handleSyncCatalog}
              disabled={catalogSyncing}
            >
              {catalogSyncing ? 'Syncing...' : 'Sync from QB'}
            </button>
            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {[
                { key: '', label: 'All' },
                { key: 'feed', label: 'Feed' },
                { key: 'packaging', label: 'Packaging' },
                { key: 'supplement', label: 'Supplements' },
                { key: 'service', label: 'Services' },
                { key: 'other', label: 'Other' },
              ].map(ft => (
                <button
                  key={ft.key}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    backgroundColor: catalogTypeFilter === ft.key ? '#2D5A27' : '#f0f0f0',
                    color: catalogTypeFilter === ft.key ? '#fff' : '#666',
                  }}
                  onClick={() => setCatalogTypeFilter(ft.key)}
                >
                  {ft.label}
                  {ft.key && catalogTypeCounts[ft.key] ? ` (${catalogTypeCounts[ft.key]})` : ft.key === '' ? ` (${catalogProducts.length})` : ''}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search by name or SKU..."
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '0.875rem',
                minWidth: '200px',
                flex: '1 1 200px',
              }}
            />
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>SKU</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Unit</th>
                <th style={styles.th}>Add-On</th>
                <th style={styles.th}>QB Linked</th>
                <th style={styles.th}>Active</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCatalogProducts.map(product => (
                <tr
                  key={product.id}
                  style={{
                    ...styles.tr,
                    backgroundColor: product.product_type === 'other' ? '#fffde7' : undefined,
                  }}
                >
                  <td style={styles.td}>
                    <div style={styles.formulaName}>{product.name}</div>
                    {product.feed_formula_name && (
                      <div style={styles.formulaSku}>Feed: {product.feed_formula_name}</div>
                    )}
                    {product.qb_item_type && (
                      <div style={styles.formulaSku}>QB: {product.qb_item_type}</div>
                    )}
                  </td>
                  {editingCatalogId === product.id ? (
                    <>
                      <td style={styles.td}>
                        <select
                          value={catalogEditValues.product_type}
                          onChange={(e) => setCatalogEditValues({ ...catalogEditValues, product_type: e.target.value })}
                          style={{ ...styles.filterSelect, width: '100%', padding: '0.375rem' }}
                        >
                          <option value="feed">Feed</option>
                          <option value="packaging">Packaging</option>
                          <option value="supplement">Supplement</option>
                          <option value="service">Service</option>
                          <option value="ingredient">Ingredient</option>
                          <option value="other">Other</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          value={catalogEditValues.sku}
                          onChange={(e) => setCatalogEditValues({ ...catalogEditValues, sku: e.target.value })}
                          style={{ ...styles.editInput, width: '100px' }}
                          placeholder="SKU"
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={catalogEditValues.default_price}
                          onChange={(e) => setCatalogEditValues({ ...catalogEditValues, default_price: e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0) {
                              setCatalogEditValues({ ...catalogEditValues, default_price: val.toFixed(2) });
                            }
                          }}
                          style={styles.editInput}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          value={catalogEditValues.unit}
                          onChange={(e) => setCatalogEditValues({ ...catalogEditValues, unit: e.target.value })}
                          style={{ ...styles.editInput, width: '70px' }}
                        />
                      </td>
                      <td style={styles.td}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8125rem' }}>
                          <input
                            type="checkbox"
                            checked={catalogEditValues.sellable_as_addon === 'true'}
                            onChange={(e) => setCatalogEditValues({
                              ...catalogEditValues,
                              sellable_as_addon: e.target.checked ? 'true' : 'false',
                            })}
                          />
                          Offer on orders
                        </label>
                        {catalogEditValues.sellable_as_addon === 'true' && (
                          UNIT_IS_WEIGHT(catalogEditValues.unit) ? (
                            <div style={{ fontSize: '0.75rem', color: '#2D5A27', marginTop: '0.25rem' }}>
                              Sold by the pound
                            </div>
                          ) : (
                            <div style={{ marginTop: '0.25rem' }}>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={catalogEditValues.addon_weight_lbs}
                                onChange={(e) => setCatalogEditValues({
                                  ...catalogEditValues,
                                  addon_weight_lbs: e.target.value,
                                })}
                                style={{ ...styles.editInput, width: '70px' }}
                                placeholder="lb each"
                              />
                              <div style={{ fontSize: '0.7rem', color: '#999' }}>lb per item</div>
                            </div>
                          )
                        )}
                      </td>
                      <td style={styles.td}>
                        {product.qb_item_id ? (
                          <span style={{ color: '#2D5A27', fontWeight: 500, fontSize: '0.8125rem' }}>Linked</span>
                        ) : (
                          <span style={{ color: '#999', fontSize: '0.8125rem' }}>No</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={{ color: product.is_active ? '#2D5A27' : '#999', fontSize: '0.8125rem' }}>
                          {product.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.saveBtn}
                          onClick={() => handleSaveCatalogProduct(product.id)}
                          disabled={saving}
                        >
                          {saving ? '...' : 'Save'}
                        </button>
                        <button
                          style={styles.cancelBtn}
                          onClick={() => setEditingCatalogId(null)}
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={styles.td}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: product.product_type === 'feed' ? '#e8f5e9' :
                            product.product_type === 'packaging' ? '#e3f2fd' :
                            product.product_type === 'supplement' ? '#fce4ec' :
                            product.product_type === 'service' ? '#f3e5f5' :
                            '#fff3e0',
                          color: product.product_type === 'feed' ? '#2e7d32' :
                            product.product_type === 'packaging' ? '#1565c0' :
                            product.product_type === 'supplement' ? '#c62828' :
                            product.product_type === 'service' ? '#6a1b9a' :
                            '#e65100',
                        }}>
                          {product.product_type}
                        </span>
                      </td>
                      <td style={styles.td}>{product.sku || '-'}</td>
                      <td style={styles.td}>{formatCurrency(parseFloat(product.default_price))}</td>
                      <td style={styles.td}>{product.unit}</td>
                      <td style={styles.td}>
                        {product.sellable_as_addon ? (
                          <>
                            <span style={{ color: '#2D5A27', fontWeight: 600, fontSize: '0.8125rem' }}>
                              Offered
                            </span>
                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                              {product.priced_by_weight
                                ? 'by the pound'
                                : product.addon_weight_lbs
                                  ? `${parseFloat(product.addon_weight_lbs)} lb each`
                                  : 'no weight on file'}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: '#bbb', fontSize: '0.8125rem' }}>—</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        {product.qb_item_id ? (
                          <span style={{ color: '#2D5A27', fontWeight: 500, fontSize: '0.8125rem' }}>Linked</span>
                        ) : (
                          <span style={{ color: '#999', fontSize: '0.8125rem' }}>No</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => handleToggleCatalogActive(product)}
                          style={{
                            border: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            backgroundColor: product.is_active ? '#e8f5e9' : '#f5f5f5',
                            color: product.is_active ? '#2e7d32' : '#999',
                          }}
                        >
                          {product.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.editBtn}
                          onClick={() => handleEditCatalogProduct(product)}
                        >
                          Edit
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {filteredCatalogProducts.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ ...styles.td, textAlign: 'center', color: '#888', padding: '2rem' }}>
                    {catalogProducts.length === 0
                      ? 'No products yet. Click "Sync from QB" to import your QuickBooks items.'
                      : 'No products match your filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Changed By</th>
                <th style={styles.th}>Item</th>
                <th style={styles.th}>Changes</th>
                <th style={styles.th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {history.map(change => (
                <tr key={change.id} style={styles.tr}>
                  <td style={styles.td}>
                    {new Date(change.changed_at).toLocaleDateString()}{' '}
                    {new Date(change.changed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={styles.td}>{change.changed_by}</td>
                  <td style={styles.td}>
                    <div style={styles.formulaName}>{change.entity_name}</div>
                    <div style={styles.formulaSku}>{change.entity_type}</div>
                  </td>
                  <td style={styles.td}>
                    {Object.entries(change.changes).map(([field, vals]) => {
                      if (field === 'details' || field === 'action') return null;
                      const v = vals as { old: number | null; new: number | null };
                      return (
                        <div key={field} style={styles.changeItem}>
                          <span style={styles.changeField}>{field}:</span>{' '}
                          <span style={styles.changeOld}>{v.old ?? '-'}</span>
                          <span style={styles.changeArrow}>&rarr;</span>
                          <span style={styles.changeNew}>{v.new ?? '-'}</span>
                        </div>
                      );
                    })}
                  </td>
                  <td style={styles.td}>{change.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .price-mgmt-container {
            padding: 1rem !important;
          }
          .price-mgmt-header {
            flex-direction: column !important;
            gap: 1rem !important;
            align-items: stretch !important;
          }
          .price-mgmt-header h1 {
            font-size: 1.5rem !important;
          }
          .price-mgmt-tabs {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }
          .price-mgmt-tabs button {
            min-height: 44px !important;
            white-space: nowrap !important;
            padding: 0.5rem 1rem !important;
          }
          .price-mgmt-stats {
            grid-template-columns: 1fr 1fr !important;
            gap: 0.75rem !important;
          }
          .price-mgmt-table-container {
            margin: 0 -1rem !important;
            border-radius: 0 !important;
          }
          .price-mgmt-filter select {
            width: 100% !important;
            min-height: 48px !important;
            font-size: 16px !important;
          }
          .price-mgmt-modal-overlay {
            padding: 0 !important;
            align-items: stretch !important;
          }
          .price-mgmt-modal {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            max-width: none !important;
            border-radius: 0 !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .price-mgmt-modal input,
          .price-mgmt-modal select {
            font-size: 16px !important;
            min-height: 48px !important;
          }
          .price-mgmt-modal-actions {
            flex-direction: column !important;
            gap: 0.5rem !important;
          }
          .price-mgmt-modal-actions button {
            width: 100% !important;
            min-height: 48px !important;
          }
        }
      `}</style>

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div style={styles.modalOverlay} className="price-mgmt-modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div style={styles.modal} className="price-mgmt-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Bulk Price Update</h2>
            <p style={styles.modalDesc}>
              Apply a percentage increase or decrease to {activeTab === 'formulas' ? 'feed formula' : 'Store Club product'} prices.
            </p>

            {activeTab === 'formulas' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Price Field</label>
                <select
                  style={styles.select}
                  value={bulkField}
                  onChange={(e) => setBulkField(e.target.value)}
                >
                  <option value="price_bag_mash">Bag Price (Mash)</option>
                  <option value="price_bag_pellet">Bag Price (Pellet)</option>
                  <option value="price_byoc_mash">BYOC Price (Mash)</option>
                  <option value="price_byoc_pellet">BYOC Price (Pellet)</option>
                  <option value="price_1000lb_mash">1000-1999 lb Price (Mash)</option>
                  <option value="price_1000lb_pellet">1000-1999 lb Price (Pellet)</option>
                  <option value="price_2000lb_mash">2000+ lb Price (Mash)</option>
                  <option value="price_2000lb_pellet">2000+ lb Price (Pellet)</option>
                </select>
              </div>
            )}

            {activeTab === 'products' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Category (optional)</label>
                <select
                  style={styles.select}
                  value={bulkCategory}
                  onChange={(e) => setBulkCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {uniqueCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Percentage Change</label>
              <div style={styles.percentageInput}>
                <input
                  type="text"
                  inputMode="decimal"
                  style={styles.input}
                  value={bulkPercentage}
                  onChange={(e) => setBulkPercentage(e.target.value)}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val)) {
                      setBulkPercentage(String(val));
                    }
                  }}
                  placeholder="e.g., 5 for +5%, -3 for -3%"
                />
                <span style={styles.percentSign}>%</span>
              </div>
              <div style={styles.hint}>
                Use positive numbers to increase, negative to decrease
              </div>
            </div>

            <div style={styles.modalActions} className="price-mgmt-modal-actions">
              <button
                style={styles.cancelBtn}
                onClick={() => setShowBulkModal(false)}
              >
                Cancel
              </button>
              <button
                style={styles.applyBtn}
                onClick={handleBulkUpdate}
                disabled={saving}
              >
                {saving ? 'Applying...' : 'Apply Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Settings Modal */}
      {showSettingsModal && (
        <div style={styles.modalOverlay} className="price-mgmt-modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '560px' }} className="price-mgmt-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Markup Factors</h2>
            <p style={styles.modalDesc}>
              These values are used when recalculating suggested prices from ingredient costs.
            </p>

            {settingsLoading && !pricingSettings ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading settings...</div>
            ) : (
              <>
                {/* Group 1: Production Costs */}
                <div style={styles.settingsGroup}>
                  <div style={styles.settingsGroupTitle}>Production Costs</div>
                  <div style={styles.settingsRow}>
                    <div style={styles.settingsField}>
                      <label style={styles.label}>Pelleting Cost</label>
                      <div style={styles.inputWithUnit}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.settingsInput}
                          value={settingsValues.pelleting_cost_per_lb}
                          onChange={(e) => setSettingsValues({ ...settingsValues, pelleting_cost_per_lb: e.target.value })}
                        />
                        <span style={styles.inputSuffix}>/lb</span>
                      </div>
                    </div>
                    <div style={styles.settingsField}>
                      <label style={styles.label}>Taxes</label>
                      <div style={styles.inputWithUnit}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.settingsInput}
                          value={settingsValues.taxes_per_lb}
                          onChange={(e) => setSettingsValues({ ...settingsValues, taxes_per_lb: e.target.value })}
                        />
                        <span style={styles.inputSuffix}>/lb</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Group 2: Bag Costs */}
                <div style={styles.settingsGroup}>
                  <div style={styles.settingsGroupTitle}>Bag Costs</div>
                  <div style={styles.settingsRow}>
                    <div style={styles.settingsField}>
                      <label style={styles.label}>Bag Material</label>
                      <div style={styles.inputWithUnit}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.settingsInput}
                          value={settingsValues.bag_material_cost}
                          onChange={(e) => setSettingsValues({ ...settingsValues, bag_material_cost: e.target.value })}
                        />
                        <span style={styles.inputSuffix}>/bag</span>
                      </div>
                    </div>
                    <div style={styles.settingsField}>
                      <label style={styles.label}>Labor</label>
                      <div style={styles.inputWithUnit}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.settingsInput}
                          value={settingsValues.labor_per_bag}
                          onChange={(e) => setSettingsValues({ ...settingsValues, labor_per_bag: e.target.value })}
                        />
                        <span style={styles.inputSuffix}>/bag</span>
                      </div>
                    </div>
                    <div style={styles.settingsField}>
                      <label style={styles.label}>Min Profit</label>
                      <div style={styles.inputWithUnit}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.settingsInput}
                          value={settingsValues.bag_profit_min}
                          onChange={(e) => setSettingsValues({ ...settingsValues, bag_profit_min: e.target.value })}
                        />
                        <span style={styles.inputSuffix}>/bag</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Group 3: Profit Margins */}
                <div style={styles.settingsGroup}>
                  <div style={styles.settingsGroupTitle}>Profit Margins</div>
                  <div style={styles.settingsRow}>
                    <div style={styles.settingsField}>
                      <label style={styles.label}>BYOC</label>
                      <div style={styles.inputWithUnit}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.settingsInput}
                          value={settingsValues.profit_per_lb_byoc}
                          onChange={(e) => setSettingsValues({ ...settingsValues, profit_per_lb_byoc: e.target.value })}
                        />
                        <span style={styles.inputSuffix}>/lb</span>
                      </div>
                    </div>
                    <div style={styles.settingsField}>
                      <label style={styles.label}>1000-1999 lb</label>
                      <div style={styles.inputWithUnit}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.settingsInput}
                          value={settingsValues.profit_per_lb_1000}
                          onChange={(e) => setSettingsValues({ ...settingsValues, profit_per_lb_1000: e.target.value })}
                        />
                        <span style={styles.inputSuffix}>/lb</span>
                      </div>
                    </div>
                    <div style={styles.settingsField}>
                      <label style={styles.label}>2000+ lb</label>
                      <div style={styles.inputWithUnit}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={styles.settingsInput}
                          value={settingsValues.profit_per_lb_2000}
                          onChange={(e) => setSettingsValues({ ...settingsValues, profit_per_lb_2000: e.target.value })}
                        />
                        <span style={styles.inputSuffix}>/lb</span>
                      </div>
                    </div>
                  </div>
                </div>

                {pricingSettings?.updated_at && (
                  <div style={styles.settingsFooter}>
                    Last updated: {new Date(pricingSettings.updated_at).toLocaleDateString()}{' '}
                    {pricingSettings.updated_by && `by ${pricingSettings.updated_by}`}
                  </div>
                )}

                <div style={styles.modalActions} className="price-mgmt-modal-actions">
                  <button
                    style={styles.cancelBtn}
                    onClick={() => setShowSettingsModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    style={styles.applyBtn}
                    onClick={handleSaveSettings}
                    disabled={settingsLoading}
                  >
                    {settingsLoading ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    margin: 0,
  },
  headerButtons: {
    display: 'flex',
    gap: '0.75rem',
  },
  settingsButton: {
    backgroundColor: '#fff',
    color: '#2D5A27',
    border: '2px solid #2D5A27',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  bulkButton: {
    backgroundColor: '#2D5A27',
    color: '#fff',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  statCard: {
    backgroundColor: '#fff',
    padding: '1.25rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '2px solid transparent',
  },
  statLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statValue: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    margin: '0.25rem 0',
  },
  statSubtext: {
    fontSize: '0.8125rem',
    color: '#888',
  },
  statMargin: {
    fontSize: '0.8125rem',
    color: '#2D5A27',
    fontWeight: 500,
    marginTop: '0.5rem',
  },
  tabs: {
    display: 'flex',
    gap: '0.25rem',
    borderBottom: '2px solid #e0e0e0',
    marginBottom: '1.5rem',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#666',
    fontSize: '0.9375rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
  },
  tabActive: {
    color: '#2D5A27',
    borderBottomColor: '#2D5A27',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  filterRow: {
    padding: '1rem',
    borderBottom: '1px solid #e0e0e0',
  },
  filterSelect: {
    padding: '0.5rem 1rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.875rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '0.875rem 1rem',
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #e0e0e0',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '0.875rem 1rem',
    fontSize: '0.9375rem',
    color: '#333',
  },
  formulaName: {
    fontWeight: 500,
  },
  formulaSku: {
    fontSize: '0.75rem',
    color: '#888',
  },
  perLbText: {
    fontSize: '0.6875rem',
    color: '#666',
    marginTop: '2px',
  },
  costValue: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    color: '#2D5A27',
    fontWeight: 500,
  },
  noCost: {
    color: '#999',
    fontStyle: 'italic',
  },
  varianceIndicator: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    marginTop: '2px',
    padding: '1px 4px',
    borderRadius: '3px',
    display: 'inline-block',
  },
  varianceHigh: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  varianceLow: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
  },
  varianceNormal: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  recalcBtn: {
    backgroundColor: '#3B82F6',
    color: '#fff',
    border: 'none',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginLeft: '0.5rem',
  },
  byocBadge: {
    display: 'inline-block',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    marginTop: '0.25rem',
  },
  editInput: {
    width: '80px',
    padding: '0.375rem 0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.875rem',
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    flexWrap: 'wrap',
  },
  lockBtnLocked: {
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  lockBtnUnlocked: {
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  editBtn: {
    backgroundColor: 'transparent',
    color: '#2D5A27',
    border: '1px solid #2D5A27',
    padding: '0.375rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8125rem',
    cursor: 'pointer',
  },
  editBtnDisabled: {
    backgroundColor: 'transparent',
    color: '#aaa',
    border: '1px solid #ddd',
    padding: '0.375rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8125rem',
    cursor: 'not-allowed',
  },
  saveBtn: {
    backgroundColor: '#2D5A27',
    color: '#fff',
    border: 'none',
    padding: '0.375rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8125rem',
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  cancelBtn: {
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    padding: '0.375rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8125rem',
    cursor: 'pointer',
  },
  changeItem: {
    fontSize: '0.8125rem',
    marginBottom: '0.25rem',
  },
  changeField: {
    color: '#666',
  },
  changeOld: {
    color: '#e53935',
    textDecoration: 'line-through',
  },
  changeArrow: {
    color: '#888',
    margin: '0 0.375rem',
  },
  changeNew: {
    color: '#43a047',
    fontWeight: 500,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '2rem',
    maxWidth: '480px',
    width: '90%',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    margin: '0 0 0.5rem 0',
  },
  modalDesc: {
    color: '#666',
    marginBottom: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.375rem',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '0.9375rem',
  },
  input: {
    flex: 1,
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '0.9375rem',
  },
  percentageInput: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  percentSign: {
    color: '#666',
    fontWeight: 500,
  },
  hint: {
    fontSize: '0.75rem',
    color: '#888',
    marginTop: '0.375rem',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },
  applyBtn: {
    backgroundColor: '#2D5A27',
    color: '#fff',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  settingsGroup: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
  },
  settingsGroupTitle: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#2D5A27',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.75rem',
  },
  settingsRow: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  settingsField: {
    flex: '1 1 120px',
    minWidth: '120px',
  },
  inputWithUnit: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  inputPrefix: {
    padding: '0.5rem 0.5rem 0.5rem 0.75rem',
    color: '#666',
    backgroundColor: '#f5f5f5',
    borderRight: '1px solid #ddd',
  },
  settingsInput: {
    flex: 1,
    padding: '0.5rem',
    border: 'none',
    fontSize: '0.9375rem',
    minWidth: '60px',
    outline: 'none',
  },
  inputSuffix: {
    padding: '0.5rem 0.75rem 0.5rem 0.5rem',
    color: '#888',
    fontSize: '0.8125rem',
  },
  settingsFooter: {
    fontSize: '0.75rem',
    color: '#888',
    textAlign: 'center',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e0e0e0',
  },
};

export default PriceManagement;
